import { useEffect, useRef, useState, type ReactNode } from 'react'
import { isDisposableEmail } from '@webbuilder/shared-types'
import { captchaOption } from '../lib/turnstile'
import { useI18n } from '../lib/i18n'
import { supabase } from '../lib/supabase'
import type { VelticaTheme } from '@webbuilder/ui'
import { suggestEmail, domainHasMx, isValidEmailFormat } from '../lib/emailCheck'
import { beginAuthAttempt, cancelAuthAttempt, getLastAuthProvider } from '../lib/lastAuthProvider'
import { googleIdentityConfigured, signInWithGoogleIdentity } from '../lib/googleIdentity'
import { CodeInput, CODE_LENGTH } from '../components/CodeInput'
import { IconArrowLeft, IconEye, IconEyeOff, IconLoader2, IconMoon, IconSun } from '@tabler/icons-react'
import { LANGUAGES } from '@webbuilder/i18n'
import { LanguageSuggestion } from '../components/LanguageSuggestion'
import { readScheduledDeletion } from '../lib/accountDeletionNotice'
import { VelticaMascot } from '@webbuilder/ui'
import { useScrollTopOnStep } from '../lib/useScrollTopOnStep'

function GoogleIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
      <path fill="#FFC107" d="M43.611 20.083H42V20H24v8h11.303c-1.649 4.657-6.08 8-11.303 8-6.627 0-12-5.373-12-12s5.373-12 12-12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 12.955 4 4 12.955 4 24s8.955 20 20 20 20-8.955 20-20c0-1.341-.138-2.65-.389-3.917z" />
      <path fill="#FF3D00" d="M6.306 14.691l6.571 4.819C14.655 15.108 18.961 12 24 12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4c-7.682 0-14.344 4.337-17.694 10.691z" />
      <path fill="#4CAF50" d="M24 44c5.166 0 9.86-1.977 13.409-5.192l-6.19-5.238C29.211 35.091 26.715 36 24 36c-5.202 0-9.619-3.317-11.283-7.946l-6.522 5.025C9.505 39.556 16.227 44 24 44z" />
      <path fill="#1976D2" d="M43.611 20.083H42V20H24v8h11.303c-.792 2.237-2.231 4.166-4.087 5.571l6.19 5.238C41.773 34.794 44 29.659 44 24c0-1.341-.138-2.65-.389-3.917z" />
    </svg>
  )
}

type AuthVariant = 'login' | 'signup'

/**
 * La pieza de marca del acceso: una foto y el mensaje.
 *
 * Cada entrada tiene la suya: quien vuelve ve la barra de un café con el
 * portátil abierto (`login-back.jpg`); quien crea la cuenta, la sala
 * (`login.jpg`).
 *
 * Una sala cálida y en calma, con el portátil abierto en la mesa: el negocio
 * funcionando sin que haga falta estar encima. El texto va al pie, en blanco
 * sobre un degradado que sube desde abajo. Igual en claro y en oscuro: es una
 * pieza de marca, no parte del tema.
 */
function AuthHeroArtwork({ variant }: { variant: AuthVariant }) {
  const { t } = useI18n()
  const prefix = variant === 'signup' ? 'auth.hero.signup' : 'auth.hero.login'
  return (
    <div className="login-hero-visual" data-variant={variant}>
      <img className="login-hero-photo" src={variant === 'signup' ? '/login.jpg' : '/login-back.jpg'} alt="" decoding="async" />
      <div className="login-hero-copy">
        <h2>{t(`${prefix}Title`)}</h2>
        <p>{t(`${prefix}Body`)}</p>
        <ul className="login-hero-pillars">
          <li>{t('auth.hero.site')}</li>
          <li>{t('auth.hero.payments')}</li>
          <li>{t('auth.hero.bookings')}</li>
        </ul>
      </div>
    </div>
  )
}

export function AuthLayout({ children, footer, variant = 'login', theme, onToggleTheme }: {
  children: ReactNode
  footer?: ReactNode
  variant?: AuthVariant
  theme?: VelticaTheme
  onToggleTheme?: () => void
}) {
  const { t, language, setLanguage } = useI18n()
  const isSignup = variant === 'signup'

  return (
    <main className="login-page">
      <section className="login-form-section" aria-label={t('auth.accessAria')}>
        {/* El idioma se elige ANTES de entrar, y por eso está aquí.
            En el resto del panel vive en el menú de la persona, pero a ese menú
            sólo se llega con la sesión abierta: quien llega a esta pantalla en
            un idioma que no entiende no tenía dónde cambiarlo. Es la misma razón
            por la que el interruptor de tema está aquí y no allí. */}
        {/* La marca en la esquina, a la altura del idioma y el tema: el
            formulario queda solo en el centro, sin nada que compita encima. La
            mascota delante del nombre, como en la barra del panel, y en el azul
            de la marca (`brand`): aquí hace de logotipo. */}
        <a className="login-brand-mark" href="/" aria-label="véltica">
          <VelticaMascot brand size={24} />
          <img src="/veltica.svg" alt="" />
        </a>
        <div className="login-language" role="group" aria-label={t('language.label')}>
          {LANGUAGES.map((option) => (
            <button
              key={option}
              type="button"
              className={`login-language-option${option === language ? ' is-active' : ''}`}
              aria-pressed={option === language}
              /* El nombre entero para quien no ve: «ES» a secas no dice nada
                 leído en voz alta. */
              aria-label={t(`language.${option}`)}
              /* Y `lang` para que lo LEA en ese idioma. Sin esto, un lector de
                 pantalla en español pronuncia «EN» como si fuera español, que
                 es justo el botón que necesita entender quien no lo habla. */
              lang={option}
              onClick={() => setLanguage(option)}
            >
              {option.toUpperCase()}
            </button>
          ))}
        </div>
        {theme && onToggleTheme ? (
          <button
            className="login-theme-toggle"
            type="button"
            onClick={onToggleTheme}
            aria-label={theme === 'dark' ? t('auth.switchToLight') : t('auth.switchToDark')}
            title={theme === 'dark' ? t('auth.lightMode') : t('auth.darkMode')}
          >
            {theme === 'dark' ? <IconSun size={17} /> : <IconMoon size={17} />}
          </button>
        ) : null}
        <div className="login-form-shell">
          {/* Va dentro de la columna del formulario y no flotando arriba: es
              una sugerencia sobre lo que se está leyendo, así que se lee en el
              mismo sitio. */}
          <LanguageSuggestion />
          <div className="login-shell-body">
            {children}
          </div>
          {footer ? <div className="login-shell-footer">{footer}</div> : null}
        </div>
      </section>

      <section className="login-hero-section" aria-label={t('auth.previewAria')}>
        <AuthHeroArtwork variant={isSignup ? 'signup' : 'login'} />
      </section>
    </main>
  )
}

// Single, provider-neutral email flow: the user only chooses "correo" or Google.
// After the email we look it up and, without telling the user whether the account
// existed, either send a code (returning user) or ask for a name first (new user).
// Verifying the emailed code both proves the address is real and signs them in.
type LoginStep = 'choose' | 'name' | 'code'

const RESEND_COOLDOWN = 30
// Only the email address is remembered (never a password or session) — a
// convenience so a returning user doesn't retype it. Cleared when unchecked.
const REMEMBER_EMAIL_KEY = 'veltica_remember_email'

function persistRememberedEmail(remember: boolean, value: string) {
  try {
    if (remember && value.trim()) localStorage.setItem(REMEMBER_EMAIL_KEY, value.trim())
    else localStorage.removeItem(REMEMBER_EMAIL_KEY)
  } catch { /* storage unavailable (private mode) — ignore */ }
}

function AuthEntry({ variant, theme, onToggleTheme }: {
  variant: AuthVariant
  theme: VelticaTheme
  onToggleTheme: () => void
}) {
  const { t, fmt } = useI18n()
  const [step, setStep] = useState<LoginStep>('choose')
  useScrollTopOnStep(step)
  const [email, setEmail] = useState(() => {
    try { return localStorage.getItem(REMEMBER_EMAIL_KEY) ?? '' } catch { return '' }
  })
  // Checked by default when we already have a remembered email on this device.
  const [rememberEmail, setRememberEmail] = useState(() => {
    try { return Boolean(localStorage.getItem(REMEMBER_EMAIL_KEY)) } catch { return false }
  })
  const [name, setName] = useState('')
  const [code, setCode] = useState('')
  const [isNewAccount, setIsNewAccount] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState<'continue' | 'google' | 'verify' | 'resend' | null>(null)
  const [resendIn, setResendIn] = useState(0)
  const [lastAuthProvider] = useState(getLastAuthProvider)
  /* Si se acaba de pedir la eliminación de la cuenta en esta pestaña. */
  const [scheduledDeletion] = useState(readScheduledDeletion)
  const emailSuggestion = suggestEmail(email)
  const emailInputRef = useRef<HTMLInputElement>(null)
  const nameInputRef = useRef<HTMLInputElement>(null)

  /* Al volver de «Usar otro correo» el foco va al correo; al entrar, no: en
     la primera pantalla también está Google y no hay por qué elegir por nadie. */
  const backToEmail = useRef(false)
  useEffect(() => {
    if (step === 'choose' && backToEmail.current) {
      backToEmail.current = false
      emailInputRef.current?.focus()
    }
    if (step === 'name') nameInputRef.current?.focus()
  }, [step])

  useEffect(() => {
    if (resendIn <= 0) return
    const timer = window.setInterval(() => setResendIn((value) => (value <= 1 ? 0 : value - 1)), 1000)
    return () => window.clearInterval(timer)
  }, [resendIn])

  const busy = loading !== null

  function resetToEmail() {
    setError(null)
    setCode('')
    setName('')
    setIsNewAccount(false)
    setResendIn(0)
    backToEmail.current = true
    setStep('choose')
  }

  async function emailDeliverable(): Promise<boolean> {
    const normalizedEmail = email.trim()
    if (!isValidEmailFormat(normalizedEmail)) {
      setError(t('auth.error.badEmail'))
      return false
    }
    const mx = await domainHasMx(normalizedEmail.slice(normalizedEmail.lastIndexOf('@') + 1))
    if (mx === false) {
      setError(t('auth.error.noMx'))
      return false
    }
    return true
  }

  // Sends the one-time code and, for new accounts, carries the name so it lands
  // in the user's metadata once the code is verified.
  async function sendCode(createUser: boolean): Promise<boolean> {
    /* Una cuenta nueva con un correo desechable, no: es la forma de crear
       cuentas gratis sin fin. El worker además les quita la IA. */
    if (createUser && isDisposableEmail(email)) {
      setError(t('auth.error.disposable'))
      return false
    }
    beginAuthAttempt('email')
    const { error: otpError } = await supabase.auth.signInWithOtp({
      email: email.trim(),
      options: {
        ...await captchaOption(),
        shouldCreateUser: createUser,
        emailRedirectTo: window.location.href,
        ...(createUser ? { data: { full_name: name.trim() } } : {}),
      },
    })
    if (otpError) {
      cancelAuthAttempt('email')
      setError(otpError.message)
      return false
    }
    setResendIn(RESEND_COOLDOWN)
    return true
  }

  async function handleEmailContinue(event: React.FormEvent) {
    event.preventDefault()
    setError(null)
    setLoading('continue')

    if (!(await emailDeliverable())) {
      setLoading(null)
      return
    }

    // Remember (or forget) the email now that we know it's a real address.
    persistRememberedEmail(rememberEmail, email)

    const lookup = await supabase.rpc('email_exists', { p_email: email.trim() })

    if (lookup.error) {
      setLoading(null)
      setError(t('auth.error.generic'))
      return
    }

    if (lookup.data === true) {
      setIsNewAccount(false)
      const sent = await sendCode(false)
      setLoading(null)
      if (sent) setStep('code')
      return
    }

    // Unknown email → treat it as a new account, but never say so out loud.
    setIsNewAccount(true)
    setLoading(null)
    setStep('name')
  }

  async function handleNameContinue(event: React.FormEvent) {
    event.preventDefault()
    setError(null)
    if (!name.trim()) {
      setError(t('auth.error.needName'))
      return
    }
    setLoading('continue')
    const sent = await sendCode(true)
    setLoading(null)
    if (sent) setStep('code')
  }

  async function verifyCode(token: string) {
    if (loading === 'verify') return
    setError(null)
    if (token.length < CODE_LENGTH) {
      setError(t('auth.codeHint'))
      return
    }
    setLoading('verify')
    const { error: verifyError } = await supabase.auth.verifyOtp({
      email: email.trim(),
      token,
      type: 'email',
    })
    if (verifyError) {
      setLoading(null)
      cancelAuthAttempt('email')
      setError(t('auth.error.badCode'))
      return
    }
    // On success onAuthStateChange takes over and routes the user in.
  }

  async function handleResend() {
    if (resendIn > 0 || busy) return
    setError(null)
    setLoading('resend')
    await sendCode(isNewAccount)
    setLoading(null)
  }

  async function handleGoogleSignIn() {
    setError(null)
    setLoading('google')
    beginAuthAttempt('google')

    /*
     * Primero sin salir de aquí.
     *
     * Google pregunta en su propia ventana y devuelve el token al navegador, así
     * que en su pantalla se lee NUESTRO dominio y no el de Supabase. Si no se
     * puede —sin clave configurada, con el guion bloqueado, o si se cierra la
     * ventana sin elegir— se sigue por el camino con redirección de siempre,
     * que funciona en todas partes. Ver `lib/googleIdentity.ts`.
     */
    if (googleIdentityConfigured()) {
      const outcome = await signInWithGoogleIdentity()
      // Dentro: `onAuthStateChange` se encarga de llevar a la app.
      if (outcome === 'signed-in') return
      if (outcome === 'error') {
        cancelAuthAttempt('google')
        setError(t('auth.error.google'))
        setLoading(null)
        return
      }
    }

    /* Dentro de una ventana incrustada (la de la caja de IA de veltica.com)
       Google no se deja abrir: su página de acceso rechaza los iframes. Se
       sigue en la pestaña entera, en el acceso del panel; lo escrito ya está
       guardado y el asistente lo recoge al entrar. */
    if (window.top && window.top !== window) {
      cancelAuthAttempt('google')
      try { window.top.location.href = new URL('/signup', window.location.origin).toString() } catch { window.open('/signup', '_blank', 'noopener') }
      return
    }

    const { error: signInError } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: window.location.href,
        /*
         * Que Google pregunte SIEMPRE con qué cuenta.
         *
         * Sin esto, quien ya tiene una sesión de Google abierta en el navegador
         * y nos autorizó alguna vez entra directo con ella: Google se salta el
         * selector porque no hay ambigüedad que resolver desde su lado. Desde el
         * nuestro sí la hay —alguien que acaba de cerrar sesión suele querer
         * entrar con OTRA cuenta, y ésa es justo la razón por la que pulsa aquí—
         * y sin el selector no había manera de llegar a ella salvo cerrando la
         * sesión de Google en otra pestaña.
         *
         * `select_account` y no `consent`: lo que hace falta es elegir cuenta,
         * no volver a aceptar los permisos cada vez.
         */
        queryParams: { prompt: 'select_account' },
      },
    })
    if (signInError) {
      cancelAuthAttempt('google')
      setLoading(null)
      setError(signInError.message)
    }
  }

  if (step === 'name') {
    return (
      <AuthLayout variant={variant} theme={theme} onToggleTheme={onToggleTheme}>
        <button className="login-back-btn" type="button" disabled={busy} onClick={resetToEmail}>
          <IconArrowLeft size={17} />
          {t('auth.useAnotherEmail')}
        </button>

        <div className="login-copy">
          <h1>{t('auth.nameQuestion')}</h1>
          <p>{t('auth.nameWhy')}</p>
        </div>

        <form className="login-form" onSubmit={(event) => { void handleNameContinue(event) }}>
          <label className="login-field">
            <span>{t('auth.name')}</span>
            <div className="login-input-wrap">
              <input
                ref={nameInputRef}
                type="text"
                placeholder={t('auth.namePlaceholder')}
                value={name}
                onChange={(event) => setName(event.target.value)}
                required
                autoComplete="name"
              />
            </div>
          </label>

          {error ? <p className="login-alert login-alert--error">{error}</p> : null}

          <button className="login-primary-btn" type="submit" disabled={busy}>
            {loading === 'continue' ? <IconLoader2 className="dash-spin" size={16} /> : null}
            {loading === 'continue' ? t('auth.sendingCode') : t('auth.continue')}
          </button>
        </form>
      </AuthLayout>
    )
  }

  if (step === 'code') {
    return (
      <AuthLayout variant={variant} theme={theme} onToggleTheme={onToggleTheme}>
        <button className="login-back-btn" type="button" disabled={busy} onClick={resetToEmail}>
          <IconArrowLeft size={17} />
          {t('auth.useAnotherEmail')}
        </button>

        <div className="login-copy">
          <h1>{t('auth.checkYourEmail')}</h1>
          <p>
            {t('auth.codeSentTo', { email: '\u0000' })
              .split('\u0000')
              .flatMap((chunk, index) => (index === 0
                ? [chunk]
                : [<strong key="email">{email.trim()}</strong>, chunk]))}
          </p>
        </div>

        <form className="login-form" onSubmit={(event) => { event.preventDefault(); void verifyCode(code.trim()) }}>
          <label className="login-field">
            <span>{t('auth.codeLabel')}</span>
            <CodeInput
              value={code}
              onChange={setCode}
              onComplete={(token) => { void verifyCode(token) }}
              disabled={busy}
            />
          </label>

          {error ? <p className="login-alert login-alert--error">{error}</p> : null}

          <button className="login-primary-btn" type="submit" disabled={busy}>
            {loading === 'verify' ? <IconLoader2 className="dash-spin" size={16} /> : null}
            {loading === 'verify' ? t('auth.verifying') : t('auth.continue')}
          </button>
        </form>

        <p className="login-create">
          {t('auth.notArrived')}{' '}
          <button type="button" disabled={busy || resendIn > 0} onClick={() => { void handleResend() }}>
            {loading === 'resend'
              ? t('auth.sending')
              : resendIn > 0
                ? t('auth.resendIn', { seconds: resendIn })
                : t('auth.resendCode')}
          </button>
        </p>
      </AuthLayout>
    )
  }

  return (
    <AuthLayout
      variant={variant}
      theme={theme}
      onToggleTheme={onToggleTheme}
      footer={(
        <div className="login-footer-content">
          <span className="login-footer-divider" aria-hidden="true" />
          <p className="login-legal">
            {t('auth.legalPrefix')}{' '}
            <a href="/terminos" target="_blank" rel="noreferrer">{t('auth.terms')}</a>{' '}
            {t('auth.legalMiddle')}{' '}
            <a href="/privacidad" target="_blank" rel="noreferrer">{t('auth.privacy')}</a>.
          </p>
        </div>
      )}
    >
      {scheduledDeletion ? (
        <p className="login-alert login-alert--notice" role="status">
          {t('auth.deletionScheduled', { date: fmt.date(scheduledDeletion) })}
        </p>
      ) : null}

      <div className="login-copy">
        <h1>{variant === 'signup' ? t('auth.createAccount') : t('auth.welcomeBack')}</h1>
        <p>
          {variant === 'signup'
            ? t('auth.signUpIntro')
            : t('auth.signInIntro')}
        </p>
      </div>

      {/* Google y el correo en la misma pantalla. Antes eran dos botones
          iguales y el correo pedía otro paso sólo para escribirlo: ahora se
          escribe aquí, debajo de un «o», y «Continuar» es el único botón
          oscuro de la pantalla. «Usado recientemente» va DENTRO de su opción,
          no montado sobre el borde. */}
      <button className={`login-google-btn${lastAuthProvider === 'google' ? ' has-badge' : ''}`} type="button" disabled={busy} onClick={() => { void handleGoogleSignIn() }}>
        {loading === 'google' ? <IconLoader2 className="dash-spin" size={16} /> : <GoogleIcon />}
        {/* Nombre propio: no va al catálogo, se escribe igual en los dos. */}
        {t('auth.continueWith', { provider: 'Google' })}
        {lastAuthProvider === 'google' ? <span className="login-provider-badge">{t('auth.recentlyUsed')}</span> : null}
      </button>

      <div className="login-divider" role="separator"><span>{t('auth.or')}</span></div>

      <form className="login-form" onSubmit={(event) => { void handleEmailContinue(event) }}>
        <label className="login-field">
          <span className="login-field-label">
            {t('auth.email')}
            {lastAuthProvider === 'email' ? <em className="login-field-tag">{t('auth.recentlyUsed')}</em> : null}
          </span>
          <div className="login-input-wrap">
            <input
              ref={emailInputRef}
              type="email"
              placeholder={t('auth.emailPlaceholder')}
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              required
              autoComplete="email"
            />
          </div>
          {emailSuggestion ? (
            <button type="button" className="login-email-suggest" onClick={() => setEmail(emailSuggestion)}>
              {t('auth.didYouMean', { suggestion: '\u0000' })
                    .split('\u0000')
                    .flatMap((chunk, index) => (index === 0
                      ? [chunk]
                      : [<strong key="s">{emailSuggestion}</strong>, chunk]))}
            </button>
          ) : null}
        </label>

        <label className="login-remember">
          <input
            type="checkbox"
            checked={rememberEmail}
            onChange={(event) => {
              const next = event.target.checked
              setRememberEmail(next)
              // Clear immediately on uncheck so it won't reappear next time.
              if (!next) persistRememberedEmail(false, '')
            }}
          />
          <span>{t('auth.rememberEmail')}</span>
        </label>

        {error ? <p className="login-alert login-alert--error">{error}</p> : null}

        <button className="login-primary-btn" type="submit" disabled={busy}>
          {loading === 'continue' ? <IconLoader2 className="dash-spin" size={16} /> : null}
          {loading === 'continue' ? t('auth.continuing') : t('auth.continueWithEmail')}
        </button>
      </form>

      <p className="login-auth-switch">
        {variant === 'signup' ? t('auth.haveAccount') : t('auth.noAccount')}
        {/* Explícito: JSX se come el salto de línea entre la pregunta y el
            enlace, así que sin esto salían pegados —«¿No tienes una cuenta?Crear
            cuenta»— en los dos idiomas. Va en el marcado y no dentro del texto
            traducido: un espacio al final de una cadena se pierde en cuanto
            alguien la recorta. */}
        {' '}
        <a href={variant === 'signup' ? '/login' : '/signup'}>
          {variant === 'signup' ? t('auth.signIn') : t('auth.signUp')}
        </a>
      </p>
    </AuthLayout>
  )
}

type AuthThemeProps = { theme: VelticaTheme; onToggleTheme: () => void }

export function Login({ theme, onToggleTheme }: AuthThemeProps) {
  return <AuthEntry variant="login" theme={theme} onToggleTheme={onToggleTheme} />
}

export function SignUp({ theme, onToggleTheme }: AuthThemeProps) {
  return <AuthEntry variant="signup" theme={theme} onToggleTheme={onToggleTheme} />
}

export function PasswordReset({ hasSession, onComplete }: { hasSession: boolean; onComplete: () => void }) {
  const { t } = useI18n()
  const [password, setPassword] = useState('')
  const [confirmation, setConfirmation] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function handlePasswordUpdate(event: React.FormEvent) {
    event.preventDefault()
    setError(null)
    if (password.length < 8) {
      setError(t('auth.error.shortPassword'))
      return
    }
    if (password !== confirmation) {
      setError(t('auth.error.mismatch'))
      return
    }

    setLoading(true)
    const { error: updateError } = await supabase.auth.updateUser({ password })
    setLoading(false)
    if (updateError) setError(updateError.message)
    else onComplete()
  }

  return (
    <AuthLayout>
      <div className="login-copy">
        <h1>{t('auth.newPasswordTitle')}</h1>
        <p>{t('auth.newPasswordHint')}</p>
      </div>

      {!hasSession ? (
        <>
          <p className="login-alert login-alert--error">{t('auth.error.expiredLink')}</p>
          <button className="login-primary-btn" type="button" onClick={onComplete}>{t('auth.requestNewLink')}</button>
        </>
      ) : (
        <form className="login-form" onSubmit={(event) => { void handlePasswordUpdate(event) }}>
          <label className="login-field">
            <span>{t('auth.newPassword')}</span>
            <div className="login-input-wrap login-input-wrap--password">
              <input
                type={showPassword ? 'text' : 'password'}
                placeholder={t('auth.passwordPlaceholder')}
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                required
                autoFocus
                minLength={8}
                autoComplete="new-password"
              />
              <button type="button" aria-label={showPassword ? t('auth.hidePassword') : t('auth.showPassword')} onClick={() => setShowPassword((value) => !value)}>
                {showPassword ? <IconEyeOff size={18} /> : <IconEye size={18} />}
              </button>
            </div>
          </label>

          <label className="login-field">
            <span>{t('auth.confirmPassword')}</span>
            <div className="login-input-wrap">
              <input
                type={showPassword ? 'text' : 'password'}
                placeholder={t('auth.repeatPlaceholder')}
                value={confirmation}
                onChange={(event) => setConfirmation(event.target.value)}
                required
                minLength={8}
                autoComplete="new-password"
              />
            </div>
          </label>

          {error ? <p className="login-alert login-alert--error">{error}</p> : null}

          <button className="login-primary-btn" type="submit" disabled={loading}>
            {loading ? <IconLoader2 className="dash-spin" size={16} /> : null}
            {loading ? t('auth.saving') : t('auth.savePassword')}
          </button>
        </form>
      )}
    </AuthLayout>
  )
}
